/**
 * Settings-specific API route constants.
 *
 * Path casing must match the OpenAPI spec exactly (used by the typed API client).
 * Shared routes (auth, search, attachment, view, view pin, legacy, support login, partner links, employee)
 * are now in @m3-tech/boss-api.
 */

// ── System Settings ────────────────────────────────────────────────────
export const API_SYSTEM_SETTINGS = '/api/SystemSettings' as const
export const API_NOTIFICATION_SETTINGS = '/api/NotificationSettings' as const

// ── Personal Settings ─────────────────────────────────────────────────
export const API_PERSONAL_SETTINGS = '/api/PersonalSettings' as const

// ── Employee ──────────────────────────────────────────────────────────
export const API_EMPLOYEE = '/api/Employee' as const

// ── User Settings ─────────────────────────────────────────────────────
export const API_USER_SETTINGS = '/api/UserSettings' as const

// ── Personal Tax Settings ────────────────────────────────────────────
export const API_PERSONAL_TAX_SETTINGS = '/api/PersonalTaxSettings' as const

// ── Participant Search ────────────────────────────────────────────────
export const API_SEARCH_PARTICIPANTS = '/api/SearchParticipants' as const
export const API_SEARCH_LENDER = '/api/ParticipantSettings/searchLender' as const

// ── Templates ─────────────────────────────────────────────────────────
export const API_TEMPLATES = '/api/EmailManagement' as const
export const API_TEMPLATES_GET_ALL = '/api/EmailManagement/GetAll' as const
export const API_TEMPLATES_COPY = '/api/EmailManagement/copy' as const
export const API_TEMPLATES_TEMPLATE_TYPE = '/api/EmailManagement/GetTemplateType' as const
export const API_TEMPLATE_REMINDER_GET = '/api/EmailManagement/getReminder' as const
export const API_TEMPLATE_REMINDER_SAVE = '/api/EmailManagement/saveReminder' as const
// NLD/DRC statuses a template can start a communication from, and the per-template
// selection of them (legacy: getStatuses / getPreference / savePreference).
export const API_TEMPLATE_STATUSES = '/api/EmailManagement/getStatuses' as const
export const API_TEMPLATE_STATUS_PREFERENCE = '/api/EmailManagement/getPreference' as const
export const API_TEMPLATE_STATUS_PREFERENCE_SAVE = '/api/EmailManagement/savePreference' as const
// Asset-manager image storage for the email body editor (GET list / POST upload / DELETE).
export const API_TEMPLATE_IMAGES = '/api/EmailTemplateImages' as const
// Email template file attachments (legacy `/emailtemplateattachment`), keyed by emailTemplateId.
// GET list / POST create record / DELETE by ids.
export const API_EMAIL_TEMPLATE_ATTACHMENTS = '/api/EmailTemplateAttachment' as const
// Step 1 of the two-step upload — pushes the binary to storage and returns
// { fileName, fileSize }; the record is then created via the route above.
export const API_UPLOAD_ATTACHMENT = '/api/UploadAttachment' as const
// Per-employee feature tree powering the dynamic module-link picker (module → tab → sub-section).
export const API_EMAIL_TEMPLATE_FEATURE_BY_EMPLOYEE =
    '/api/EmailManagement/featureByEmployeeId' as const

// ── Task Templates ────────────────────────────────────────────────────
export const API_TASK_TEMPLATES = '/api/TemplatedTask/GetTemplates' as const
export const API_TASK_TEMPLATES_SEARCH = '/api/TemplatedTask/Search' as const
export const API_TASK_TEMPLATE_DETAIL = '/api/TemplatedTask/GetDeal' as const
export const API_TASK_TEMPLATE_SAVE = '/api/TemplatedTask/CreateDeal' as const
// Assign To options (legacy `GET /BrokerTypes`, TemplateManagement.js:469).
export const API_BROKER_TYPES = '/api/BrokerTypes' as const
// A task template is shared through the view behind it (`viewId`), exactly as
// legacy's ShareTemplateModal does. TemplatedTask has no copy/share routes:
// Copy is GetDeal + CreateDeal (AB#2518).
export const API_VIEW_SHARED_WITH = '/api/View/getSharedWith' as const
export const API_VIEW_SHARE = '/api/View/shareView' as const
export const API_VIEW_UNSHARE = '/api/View/unShareView' as const
export const API_VIEW_SHARE_BRANDS = '/api/ViewShareSearch/getBrands' as const
export const API_VIEW_SHARE_COMPANIES = '/api/ViewShareSearch/getCompanies' as const
export const API_VIEW_SHARE_EMPLOYEES = '/api/ViewShareSearch/getEmployees' as const

// ── Commission Password ──────────────────────────────────────────────
export const API_COMMISSION_PASSWORD = '/api/CommissionPassword' as const

// ── Security Settings ─────────────────────────────────────────────────
export const API_SECURITY_SETTINGS = '/api/SecuritySettings' as const

// ── Integration Settings ──────────────────────────────────────────────
export const API_PARTNERS_INTEGRATION_ACCESS = '/api/APIPartnersIntegrationAccess' as const

// ── Marketing Settings (Customer Portal + Marketing Info) ─────────────
export const API_MARKETING_SETTINGS = '/api/MarketingSettings' as const

// ── Feature Management ────────────────────────────────────────────────
export const API_FEATURES_ALL = '/api/Feature/all' as const
export const API_FEATURES = '/api/Feature' as const

// ── User Profile Feature Management ───────────────────────────────────
export const API_USER_PROFILES = '/api/UserProfile/all' as const
export const API_USER_PROFILE_CREATE = '/api/UserProfile/Create' as const
export const API_USER_PROFILE_COPY = '/api/UserProfile/copy' as const
export const API_USER_PROFILE_DELETE = '/api/UserProfile/Delete' as const
export const API_USER_PROFILE_CONFIGURATION = '/api/UserProfile/Configuration' as const

// Legacy feature-profile search endpoints — each returns existing-profile rows
// (Edit mode) alongside rows that could receive a new profile (Create mode).
export const API_USER_PROFILE_NETWORKS = '/api/UserProfile/GetUserProfileNetworks' as const
export const API_USER_PROFILE_AFFILIATES = '/api/UserProfile/GetUserProfileAffiliates' as const
export const API_USER_PROFILE_EMPLOYEES = '/api/UserProfile/GetUserProfileEmployees' as const
export const API_USER_PROFILE_BOSS_VERSIONS = '/api/UserProfile/GetUserProfileBossVersions' as const
export const API_USER_PROFILE_ID = '/api/UserProfile/GetUserProfileId' as const
export const API_USER_PROFILE_PROVINCES = '/api/UserProfile/GetUserProfileProvinces' as const
export const API_USER_PROFILE_FEATURES = '/api/UserProfile/{id}/features' as const

// ── Profile Photo Upload ─────────────────────────────────────────────
export const API_UPDATE_PROFILE_PHOTO = '/api/UpdateProfilePhoto' as const

// ── Gross Commission Visibility ──────────────────────────────────────
export const API_VIEW_PARENT_COMMISSION = '/api/ViewParentCommission' as const

// ── Mopolo Settings ─────────────────────────────────────────────────
export const API_MOPOLO_SETTINGS = '/api/MarketingMopoloSettings' as const

// ── Mailchimp ───────────────────────────────────────────────────────
export const API_MAILCHIMP = '/api/Mailchimp' as const

// ── Website Settings ────────────────────────────────────────────────
export const API_MARKETING_WEBSITE_SETTINGS = '/api/MarketingWebsiteSettings' as const
export const API_ADMIN_EMPLOYEE_WEBSITE_SETTINGS = '/api/admin/EmployeeWebSettings' as const
export const API_ADMIN_EMPLOYEE_CHECK_SITE_NAME =
    '/api/admin/EmployeeWebSettings/CheckSiteNameAvailability' as const
export const API_ADMIN_SEARCH = '/api/admin/Search' as const
export const API_WEBSITE_OPTOUTS = '/api/WebsiteOptouts' as const
export const API_WEBSITE_PHOTO_EMPLOYEE = '/api/WebsitePhoto/employee' as const
export const API_AWARD_IMAGE = '/api/AwardImage/award' as const
export const API_AWARD_IMAGE_DELETE = '/api/AwardImage/deleteAward' as const
export const API_BROKER_LICENSE_CITIES = '/api/BrokerLicenseCities/all' as const
export const API_BROKER_LICENSE_REGIONS = '/api/BrokerLicenseRegions/all' as const
export const API_ADMIN_OFFICE = '/api/admin/Office' as const
export const API_WEBSITE_PHOTO_OFFICE = '/api/WebsitePhoto/office' as const
export const API_ADMIN_FRANCHISE = '/api/admin/Franchise' as const
// Self-service (non-admin) office/brokerage website settings — the modern
// equivalents of legacy `/officeSettings` & `/franchiseSettings`, used by the
// `/website` Select-Website switcher.
export const API_OFFICE_SETTINGS = '/api/OfficeSettings' as const
export const API_FRANCHISE_SETTINGS = '/api/FranchiseSettings' as const
export const API_ADMIN_FRANCHISE_CHECK_SITE_NAME =
    '/api/admin/Franchise/CheckSiteNameAvailability' as const
export const API_WEBSITE_PHOTO_FRANCHISE = '/api/WebsitePhoto/franchise' as const

// ── Preset Commission ──────────────────────────────────────────────────
export const API_PRESET_COMMISSION = '/api/PresetCommission' as const

// ── Admin Preset Commission ────────────────────────────────────────────
export const API_ADMIN_BRAND_NAMES = '/api/admin/Brand/Names' as const
export const API_ADMIN_BRAND_COMPANIES = '/api/admin/Brand/GetCompanies' as const
export const API_ADMIN_BRAND_TEAM_LEADS = '/api/admin/Brand/TeamLeads' as const
export const API_ADMIN_BRAND_EMPLOYEE_NAMES = '/api/admin/Brand/GetEmployeeNames' as const
export const API_PRESET_COMMISSION_PRECHECKED_AGENTS =
    '/api/PresetCommission/PreCheckedAgents' as const
export const API_ADMIN_PRESET_COMMISSION_DEAL_OWNER =
    '/api/AdminPresetCommission/DealOwnerPresetCommission' as const
export const API_ADMIN_PRESET_COMMISSION_UPDATE_DEAL_OWNER =
    '/api/AdminPresetCommission/UpdateDealOwnerPresetCommission' as const
export const API_PRESET_COMMISSION_GET_NEW = '/api/PresetCommission/GetNewPresetCommission' as const
export const API_ADMIN_DEAL_SPLIT_INSERT_RATES =
    '/api/AdminDealSplitCommission/InsertRatesOppoReceivables' as const

// ── Data Transformation (admin) ─────────────────────────────────────
export const API_AVAILABLE_EMPLOYEES = '/api/EmailTemplateShare/getAvailableEmployees' as const
export const API_ADMIN_DATA_MAPPING_UPLOAD_FILE = '/api/AdminDataMapping/UploadFile' as const
export const API_ADMIN_DATA_MAPPING_SAVE_MAPPING = '/api/AdminDataMapping/SaveMapping' as const
export const API_ADMIN_DATA_MAPPING_INSERT_DATA =
    '/api/AdminDataMapping/InsertDataIntoOpportunity' as const
export const API_ADMIN_DATA_MAPPING_HIGHEST_PAIRS =
    '/api/AdminDataMapping/HighestMappingPairs' as const
export const API_ADMIN_DATA_MAPPING_VALIDATE_IMPORT =
    '/api/AdminDataMapping/ValidateImport' as const
export const API_ADMIN_DATA_MAPPING_GET_IMPORT_TYPES =
    '/api/AdminDataMapping/GetImportTypes' as const
export const API_ADMIN_DATA_MAPPING_DELETE_DISPLAY_QUERY_RECORDS =
    '/api/AdminDataMapping/DeleteDisplayQueryRecords' as const
export const API_ADMIN_DATA_MAPPING_INSERT_DATA_CONTACTS =
    '/api/AdminDataMapping/InsertDataIntoContacts' as const
export const API_ADMIN_DATA_MAPPING_INSERT_DATA_VELOCITY =
    '/api/AdminDataMapping/InsertDataIntoVelocityDeals' as const
export const API_ADMIN_DATA_MAPPING_INSERT_DATA_FINMO =
    '/api/AdminDataMapping/InsertDataIntoFinmoDeals' as const

// ── Data Import Summary (admin) ─────────────────────────────────────
export const API_ADMIN_DATA_MAPPING_IMPORT_SUMMARY =
    '/api/AdminDataMapping/GetDataImportSummary' as const
export const API_ADMIN_DATA_MAPPING_IMPORT_SUMMARY_DETAIL =
    '/api/AdminDataMapping/GetDataImportSummaryDetail' as const
export const API_ADMIN_DATA_MAPPING_STOP_PENDING_IMPORTS =
    '/api/AdminDataMapping/StopPendingImportRecords' as const

// ── Broker Rates ────────────────────────────────────────────────────
export const API_EMPLOYEE_RATES = '/api/EmployeeRates' as const
export const API_EMPLOYEE_RATES_SETTINGS = '/api/EmployeeRates/settings' as const

// ── Brand Rates (Admin) ─────────────────────────────────────────────
export const API_ADMIN_BRAND_RATES = '/api/admin/BrandRates' as const
export const API_ADMIN_BRAND_RATES_SETTINGS = '/api/admin/BrandRates/settings' as const

// ── Franchise (Brokerage) Rates (Admin) ─────────────────────────────
// Per-province rate grid + enable toggle for the `/website` switcher's
// brokerage panel (legacy `admin/franchise/rates` + `.../rates/settings`).
export const API_ADMIN_FRANCHISE_RATES = '/api/admin/Franchise/rates' as const
export const API_ADMIN_FRANCHISE_RATES_SETTINGS = '/api/admin/Franchise/rates/settings' as const

// ── New Preset Commission ─────────────────────────────────────────────
export const API_NEW_PRESET_COMMISSION_GET = '/api/PresetCommission/GetNewPresetCommission' as const
export const API_NEW_PRESET_COMMISSION_UPDATE =
    '/api/PresetCommission/UpdatePresetCommission' as const
export const API_PRESET_COMMISSION_PRE_CHECKED = '/api/PresetCommission/PreCheckedAgents' as const

// ── Admin Deal Split Commission ──────────────────────────────────────
export const API_INSERT_RATES_OPPO_RECEIVABLES =
    '/api/AdminDealSplitCommission/InsertRatesOppoReceivables' as const

// ── Brand Visibility ────────────────────────────────────────────────
export const API_BRAND_VISIBILITY = '/api/admin/Brand/GetVisibility' as const
export const API_BRAND_UPDATE_VISIBILITY = '/api/admin/Brand/UpdateVisibility' as const
export const API_DIVISION_AR_SELECTION = '/api/DivisionAccountReceivableSelection' as const

// ── Corporate Content (Admin Website Opt-Outs) ──────────────────────
export const API_ADMIN_WEBSITE_OPTOUTS = '/api/admin/WebsiteOptouts' as const
export const API_ADMIN_WEBSITE_OPTOUTS_SEARCH = '/api/admin/WebsiteOptouts/search' as const

// ── Land Transfer Tax (Admin) ────────────────────────────────────────
export const API_ADMIN_LAND_TRANSFER_TAX = '/api/admin/LandTransferTax' as const

// ── Auto Communication ──────────────────────────────────────────────
export const API_MARKETING_AC_SETTINGS = '/api/MarketingACSettings' as const
export const API_MARKETING_ACMA_SETTINGS = '/api/MarketingACMASettings' as const
export const API_COMMUNICATION_PROGRAMS = '/api/CommunicationPrograms' as const
export const API_IMPACT_COMMUNICATION = '/api/ImpactCommunication' as const
export const API_IMPACT_COMMUNICATION_SET_ACTIVE = '/api/ImpactCommunication/SetActiveMail' as const
export const API_IMPACT_COMMUNICATION_SET_INACTIVE =
    '/api/ImpactCommunication/SetInactiveMail' as const

// ── Admin Programs ──────────────────────────────────────────────────
export const API_ADMIN_EMPLOYEE_PROGRAM_REGISTRATION =
    '/api/admin/EmployeeProgramRegistration' as const
export const API_ADMIN_PROGRAM_TYPES =
    '/api/admin/EmployeeProgramRegistration/GetProgramTypes' as const
export const API_ADMIN_PROGRAM_ACTIVITY_LOG = '/api/admin/ProgramActivityLog' as const

// ── Custom Signature ────────────────────────────────────────────────
export const API_EMAIL_TEMPLATE_SIGNATURES = '/api/EmailTemplateSignatures' as const
export const API_EMAIL_TEMPLATE_DEFAULT_SIGNATURES = '/api/EmailTemplateDefaultSignatures' as const
export const API_EMAIL_TEMPLATE_FIELD = '/api/EmailTemplateField' as const
export const API_EMAIL_TEMPLATE_FIELD_CUSTOM_SIGNATURE =
    '/api/EmailTemplateField/customSignature' as const

// ── QA Testing ──────────────────────────────────────────────────────
export const API_AUTOMATED_COMMUNICATION_QA_SERVICE =
    '/api/AutomatedCommunication/QaService' as const
export const API_AUTOMATED_COMMUNICATIONS = '/api/AutomatedCommunications' as const
export const API_TRIGGER_EXECUTION_PREPARE_QUEUE = '/api/TriggerExecution/PrepareQueue' as const
export const API_TRIGGER_EXECUTION_PREPARE_QUEUE_BY_ID =
    '/api/TriggerExecution/PrepareQueue/{id}/{date}' as const
export const API_TRIGGER_EXECUTION_PROCESS_QUEUE = '/api/TriggerExecution/ProcessQueue' as const
export const API_QA_INSURANCE_SYNCS = '/api/QAInsuranceSyncs' as const
export const API_QA_INSURANCE_SYNCS_EXECUTE = '/api/QAInsuranceSyncs/ExecuteSync' as const
export const API_EPIC_REGULAR_LEADS = '/api/EpicRegularLeads' as const

// ── Error Management (Admin diagnostic screen — internal accounts only) ──
export const API_ERROR_MANAGEMENT_DNS = '/api/admin/errorManagement' as const
export const API_ERROR_MANAGEMENT_DIVIDE_BY_ZERO = '/api/errormanagement/dividebyzero' as const

// ── Dev Service Status (internal diagnostic screen — single account only) ──
export const API_ADMIN_REPORT_NOTIFICATION = '/api/AdminReportNotification' as const
export const API_ADMIN_REPORT_NOTIFICATION_CREDITOR_USER_COUNT =
    '/api/AdminReportNotification/GetCreditorUserCount' as const
export const API_ADMIN_REPORT_NOTIFICATION_SIM_PROCESS_LOG =
    '/api/AdminReportNotification/SimProcessLog' as const
export const API_ADMIN_REPORT_NOTIFICATION_TOGGLE_CREDITOR_INSURANCE =
    '/api/AdminReportNotification/toggleCreditorInsurance' as const

// ── Workflow Statuses (used by the task-template trigger sections) ────
export const API_WORKFLOW_STATUSES = '/api/DealSummary/workflows' as const

// ── Business Central — Transactions to review (FF-2856 / FF-2857) ─────
// Payroll Manager (96001) only. Not in @m3-tech/boss5-api-types yet — the
// paths are typed by hand in src/types/bcNeedsAttention.types.ts.
export const API_BC_NEEDS_ATTENTION_COUNTS = '/api/BCNeedsAttention/Counts' as const
export const API_BC_NEEDS_ATTENTION_PAYOUTS = '/api/BCNeedsAttention/Payouts' as const
export const API_BC_NEEDS_ATTENTION_GL = '/api/BCNeedsAttention/GL' as const
export const API_BC_NEEDS_ATTENTION_REASONS = '/api/BCNeedsAttention/Reasons' as const
export const API_BC_NEEDS_ATTENTION_TRANSACTION = '/api/BCNeedsAttention/{id}' as const
export const API_BC_NEEDS_ATTENTION_HISTORY = '/api/BCNeedsAttention/{id}/History' as const
export const API_BC_NEEDS_ATTENTION_RESEND = '/api/BCNeedsAttention/{id}/Resend' as const
export const API_BC_NEEDS_ATTENTION_VENDORS = '/api/BCNeedsAttention/{id}/Vendors' as const
